import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext(null);

// Flag read by LoginPage to show the "session expired" popup after redirect
export const SESSION_EXPIRED_KEY = 'pfp_session_expired';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const userRef = useRef(null);
  userRef.current = user;

  useEffect(() => {
    checkAuth();
  }, []);

  // Detect session expiry immediately:
  // 1) Any API call returning 401 fires 'pfp:unauthorized' (see services/api.js)
  // 2) A background check polls /auth/me every 60s while logged in,
  //    so an idle expired session bounces to login without any user action.
  useEffect(() => {
    const onUnauthorized = () => {
      if (userRef.current) {
        sessionStorage.setItem(SESSION_EXPIRED_KEY, '1');
        setUser(null);
      }
    };
    window.addEventListener('pfp:unauthorized', onUnauthorized);

    const interval = setInterval(async () => {
      if (!userRef.current) return;
      // Skip while offline: a Wi-Fi blip must not be mistaken for expiry
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      try {
        const userData = await authService.getCurrentUser();
        // getCurrentUser returns null on 401 (expected anonymous / expired).
        if (userData == null) onUnauthorized();
      } catch (err) {
        // Network errors, timeouts and 5xx (e.g. backend restart) must NOT
        // log the user out. A thrown 401 is kept as a safety net for older
        // callers that still reject instead of returning null.
        if (err?.response?.status === 401) {
          onUnauthorized();
        }
      }
    }, 60000);

    return () => {
      window.removeEventListener('pfp:unauthorized', onUnauthorized);
      clearInterval(interval);
    };
  }, []);


  // The /api/* routes can be intercepted (e.g. a maintenance nginx,or a
  // proxy error page) and return HTML with a 200. Treating that as a user
  // object leaves role undefined, which silently demotes an admin to a
  // clinic user. Only accept a shape that actually looks like a user.
  const isValidUser = (u) =>
    u && typeof u === 'object' && !Array.isArray(u) && typeof u.role === 'string';

  const checkAuth = async () => {
    try {
      const userData = await authService.getCurrentUser();
      if (userData == null) {
        // Expected anonymous: /auth/me returned 401. Leave user null quietly.
        setUser(null);
      } else if (isValidUser(userData)) {
        setUser(userData);
      } else {
        // Do not overwrite a good session with a garbage response.
        console.warn('[AuthContext] Ignored non-user response from /auth/me');
      }
    } catch (error) {
      // Only a confirmed 401 means the session is gone. A 429 (rate limited),
      // network error, timeout or 5xx must not sign the user out - otherwise a
      // burst of requests bounces them to the login page, where the same limit
      // then blocks them from logging back in.
      if (error?.response?.status === 401) {
        setUser(null);
      }
    }
    setLoading(false);
  };


  const login = async (username, password) => {
    const response = await authService.login(username, password);
    
    // If user requires mandatory 2FA setup, they are authenticated but need to complete setup
    if (response?.requiresTwoFactorSetup) {
      const userData = await authService.getCurrentUser();
      if (isValidUser(userData)) setUser(userData);
      return response;
    }
    
    // If user has existing 2FA enabled, return challenge for verification
    if (response?.requiresTwoFactor) {
      return response;
    }

    const userData = await authService.getCurrentUser();
    if (isValidUser(userData)) setUser(userData);
    return response;
  };

  const verifyTwoFactor = async (challengeToken, code) => {
    const response = await authService.verifyTwoFactor(challengeToken, code);
    const userData = await authService.getCurrentUser();
    if (isValidUser(userData)) setUser(userData);
    return response;
  };

  const logout = async () => {
    // Clear the user first. The 401 handler only flags a session as expired
    // when a user was still logged in, so doing this up front stops any
    // in-flight or polling request that 401s after logout from showing the
    // "session expired" popup on a deliberate sign-out.
    setUser(null);
    userRef.current = null;
    sessionStorage.removeItem(SESSION_EXPIRED_KEY);

    try {
      await authService.logout();
    } catch {
      // A 401 here just means the cookie was already gone - still signed out.
    } finally {
      sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    }
  };


  const value = {
    user,
    loading,
    login,
    verifyTwoFactor,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
